import { observable, action, makeObservable } from 'mobx'
import memoize from 'memoizee'
import { GetCurrentUser } from './SessionStore'
import WorkspaceStore from './WorkspaceStore'
import MemberStore from './MemberStore'
import { IAMAPI } from './API'
import Member from './models/Member'
import Workspace from './models/Workspace'
import { Id } from 'types/definitions'

/**
 * Getter for Workspace memberships.
 */
export type GetCurrentUserWorkspaceMemberships = () => Membership[]

export type FetchUserWorkspaceMemberships = (
  skipCache?: boolean,
) => Promise<Array<Membership>>

export default class WorkspaceMembershipStore {
  /**
   * The current user's workspace memberships.
   */
  @observable memberships: Membership[] = []

  constructor(
    private workspaceStore: WorkspaceStore,
    private memberStore: MemberStore,
    private api: IAMAPI,
    private getCurrentUser: GetCurrentUser,
  ) {
    makeObservable(this)
  }

  private readonly _fetchWorkspaceMemberships = memoize(
    async () => {
      return this.api.users
        .getUserWorkspaceMemberships(Id.assert(this.getCurrentUser().publicId))
        .then(
          action((memberships) => {
            const mapped: Membership[] = memberships.map((m) => ({
              workspace: this.workspaceStore.primeFullWorkspace(m.workspace),
              member: this.memberStore.primeFullMember(m.member),
            }))
            this.memberships = mapped

            return mapped.slice()
          }),
        )
    },
    {
      promise: true,
      length: 0,
    },
  )

  fetchWorkspaceMemberships: FetchUserWorkspaceMemberships = (
    skipCache = false,
  ) => {
    if (skipCache) {
      this._fetchWorkspaceMemberships.clear()
    }

    return this._fetchWorkspaceMemberships()
  }

  getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships = () =>
    this.memberships.slice()

  @action.bound
  addMembership(membership: Membership) {
    this.memberships.push(membership)
  }

  /**
   * Get a user's workspace memberships.
   */
  async getUserWorkspaceMemberships(publicId: Id, inactive?: boolean) {
    const memberships = await this.api.users.getUserWorkspaceMemberships(
      Id.assert(publicId),
      { inactive },
    )
    return memberships.map((m) => ({
      workspace: this.workspaceStore.primeFullWorkspace(m.workspace),
      member: this.memberStore.primeFullMember(m.member),
    })) as Membership[]
  }
}

/**
 * A combination of Workspace and Member.
 * Used to pull workspace memberships for a user.
 */
export interface Membership {
  /**
   * Workspace member.
   */
  member: Member
  /**
   * The workspace itself.
   */
  workspace: Workspace
}
